import React, { ComponentProps, useRef, useState } from 'react'
import { CSS, VariantProps } from '@stitches/react'
import { Popper, PopperAnchor } from '@radix-ui/react-popper'
import * as Portal from '@radix-ui/react-portal'
import { useSelect, UseSelectProps, UseSelectStateChange } from 'downshift'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'
import {
  SelectRoot,
  SelectButton,
  SelectList,
  Option,
  IconContainer,
  HelperText,
  Content,
  PopperList,
} from './Select.styles'
import { useMutationObserver } from 'hooks/useMutationObserver'
import { SpaceTokens } from 'stitches.config'

type SelectVariants = VariantProps<typeof PopperAnchor>
type SelectOwnProps = Omit<
  ComponentProps<typeof SelectButton>,
  'value' | 'defaultValue' | 'onChange'
>
export type SelectProps<T> = SelectOwnProps &
  SelectVariants & {
    label: string
    outerLabel?: (props: { children: string; htmlFor: string }) => JSX.Element
    outerLabelGap?: SpaceTokens
    placeholder?: string
    items: T[]
    defaultValue?: T
    helperText?: string
    displayLabel?: boolean
    onChange: (value: string, changes?: T | null) => void
    displayformatter?: (item: T) => string | JSX.Element
    optionFormatter?: (item: T) => string | JSX.Element
    itemToString?: (item: T | null) => string
    value?: T
    error?: boolean
    success?: boolean
    isShort?: boolean
    css?: any
    widthStyle?: 'half' | 'full'
  }

export function Select<T>({
  name,
  label,
  outerLabel,
  outerLabelGap,
  items,
  onChange,
  placeholder,
  defaultValue,
  success,
  error,
  disabled,
  helperText,
  css,
  isShort,
  displayLabel = true,
  displayformatter = (selectedItem) => <Text>{selectedItem as any}</Text>,
  optionFormatter,
  itemToString = (selectedItem) => selectedItem as unknown as string,
  value,
  widthStyle,
  ...rest
}: SelectProps<T>) {
  const selectProps: UseSelectProps<T> = {
    items,
    onSelectedItemChange: (changes: UseSelectStateChange<T>) =>
      onChange(
        // NOTE: this should be changes.inputValue, but changes.inputValue is always an empty string so we're calculating it manually as a workaround.
        changes.selectedItem ? itemToString(changes.selectedItem) : '',
        changes.selectedItem
      ),
    defaultSelectedItem: defaultValue,
    itemToString,
  }

  if (value) {
    selectProps['selectedItem'] = value
  }

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    selectedItem,
    highlightedIndex,
  } = useSelect(selectProps)

  const [side, setSide] = useState('bottom')

  const buttonRef = useRef<HTMLButtonElement>(null)
  const popperRef = useRef<HTMLDivElement>(null)

  const variantProps = {
    success,
    error,
    disabled,
  }

  useMutationObserver({
    target: popperRef,
    options: { attributes: true },
    callback: (mutationRecord) => {
      setSide((mutationRecord[0].target as any).dataset.side)
    },
  })

  return (
    <Popper>
      <SelectRoot
        css={css}
        {...variantProps}
        isOpen={isOpen}
        isShort={isShort}
        widthStyle={widthStyle ? widthStyle : 'full'}
      >
        <PopperAnchor>
          <SelectButton
            title={name}
            isFilled={!!selectedItem}
            side={side}
            isOpen={isOpen}
            {...getToggleButtonProps({
              disabled: disabled,
              ref: buttonRef as any,
              type: 'button',
            })}
            {...rest}
          >
            {displayLabel && (
              <Text
                className="select-label"
                as="label"
                size="body"
                variant="secondary"
                {...getLabelProps()}
              >
                {label}
              </Text>
            )}

            {selectedItem && (
              <Content displayLabel={displayLabel}>{displayformatter(selectedItem)}</Content>
            )}

            {!displayLabel && !selectedItem && placeholder && (
              <Text color="tertiary-400">{placeholder}</Text>
            )}

            <IconContainer>
              <Icon name="arrow-down-line" size="4" color="accent-black" />
            </IconContainer>
          </SelectButton>
        </PopperAnchor>

        <HelperText css={{ display: helperText ? 'block' : 'none' }} size="caption">
          {helperText || ''}
        </HelperText>

        <Portal.Root>
          <PopperList
            ref={popperRef}
            side="bottom"
            align="start"
            style={{ width: buttonRef.current?.offsetWidth }}
          >
            <SelectList {...getMenuProps()} isOpen={isOpen}>
              {items.map((item, index) => (
                <Option
                  {...getItemProps({ item: item, index })}
                  key={itemToString(item)}
                  selected={selectedItem && itemToString(item) === itemToString(selectedItem)}
                  highlighted={highlightedIndex === index}
                >
                  {optionFormatter ? optionFormatter(item) : itemToString(item)}
                </Option>
              ))}
            </SelectList>
          </PopperList>
        </Portal.Root>
      </SelectRoot>
    </Popper>
  )
}

Select.displayName = 'Select'
